import React, { useEffect, useState } from 'react';
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useSortBy
} from 'react-table';
import 'bootstrap/dist/css/bootstrap.min.css';
import apiClient from '../utils/apiClient';
import { SERVER_BASE } from '../config/server';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV, faEdit, faTrash, faPlus, faPencil } from "@fortawesome/free-solid-svg-icons";
import moment from 'moment';
import { useNavigate } from 'react-router-dom'; // for navigation
import Swal from 'sweetalert2';
import { hasPermission } from "../utils/usePermissions";

// Simple global filter component
const GlobalFilter = ({ globalFilter, setGlobalFilter }) => (
  <input
    className="form-control mb-2"
    value={globalFilter || ''}
    onChange={e => setGlobalFilter(e.target.value)}
    placeholder="Search..."
    style={{ width: '87%' }}

  />
);

const AreaProjectList = ({ permissions }) => {
  const [data, setData] = useState([]);
  const navigate = useNavigate();


  const handleEdit = (rowData) => {
    navigate('../addAreaProjects', { state: { rowData } });
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'Do you really want to delete this project?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        const response = await apiClient.delete(`${SERVER_BASE}/area-projects/${id}`);
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: response?.data?.message,
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true,
        });
        fetchProjects(); // refresh
      } catch (err) {
        console.error('Delete failed:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'Failed to delete project.',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      }
    }
  };
  const handleToggleStatus = async (id, currentStatus) => {
    const stat = currentStatus = parseInt(currentStatus)
    const result = await Swal.fire({
      title: `Are you sure?`,
      text: `You are about to ${stat ? 'deactivate' : 'activate'} this project.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: `Yes, ${stat ? 'Deactivate' : 'Activate'}`,
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      try {
        const res = await apiClient.put(`${SERVER_BASE}/toggleProjectStatus/${id}`);
        Swal.fire({
          icon: 'success',
          title: res?.data?.message,
          toast: true,
          position: 'top-end',
          timer: 2000,
          showConfirmButton: false,
          timerProgressBar: true,
        });
        fetchProjects(); // Refresh list
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to update status.',
          toast: true,
          position: 'top-end',
          timer: 3000,
          showConfirmButton: false,
          timerProgressBar: true,
        });
      }
    }
  };


  const fetchProjects = async () => {
    try {
      const res = await apiClient.get(`${SERVER_BASE}/area-projects`);
      setData(res.data);
    } catch (err) {
      console.error('Error loading projects', err);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const columns = React.useMemo(() => [
    {
      Header: '#',
      accessor: 'serial',
      Cell: ({ row }) => row.index + 1
    },
    { Header: 'Ledger', accessor: 'printName' },
    { Header: 'Project Name', accessor: 'projectName' },
    { Header: 'Code', accessor: 'projectCode' },
    { Header: 'Approval No', accessor: 'approvalNo' },
    {
      Header: 'Approval Date', accessor: 'approvalDate', Cell: ({ value }) =>
        value ? moment(value).format('DD-MMM-YYYY') : ''
    },
    {
      Header: 'Completion Date', accessor: 'expectedCompletionDate', Cell: ({ value }) =>
        value ? moment(value).format('DD-MMM-YYYY') : ''
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ row }) => (
        <span className={`badge ${row.original.status == '1' ? 'bg-success' : 'bg-secondary'}`}>
          {row.original.status == '1' ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      Header: 'Action',
      accessor: 'action',
      Cell: ({ row }) => {
        const {
          createdAt,
          createdByName,
          updatedAt,
          updatedByName,
        } = row.original;
        return (

          <div className="dropdown">
            <button
              className="btn btn-light btn-sm "
              type="button"
              id={`dropdownMenuButton-${row.id}`}
              data-bs-toggle="dropdown"
              aria-expanded="false"
              style={{ border: 'none' }}
            >
              <FontAwesomeIcon icon={faEllipsisV} />
            </button>
            <ul className="dropdown-menu" aria-labelledby={`dropdownMenuButton-${row.id}`}>
              {permissions?.edit && (
                <li>
                  <button className="dropdown-item" onClick={() => handleEdit(row.original)}>
                    <FontAwesomeIcon icon={faEdit} className="me-2" />
                    Edit
                  </button>
                </li>
              )}
              {permissions?.delete && (
                <li>
                  <button className="dropdown-item" onClick={() => handleDelete(row.original.id)}>
                    <FontAwesomeIcon icon={faTrash} className="me-2" />
                    Delete
                  </button>
                </li>
              )}
              {permissions?.write && (
                <li>
                  <button className="dropdown-item" onClick={() => handleToggleStatus(row.original.id, row.original.status)}>
                    {row.original.status == '1' ? 'Mark Inactive' : 'Mark Active'}
                  </button>
                </li>
              )}
              {(createdByName || updatedByName) && (
                <li>
                  <hr className="dropdown-divider" />
                </li>
              )}
              {createdByName && (
                <li className="dropdown-item">
                  <FontAwesomeIcon icon={faPlus} /> {createdByName}
                  {createdAt &&
                    ` (${moment(createdAt).format("DD-MMM-YY hh:mm A")})`}
                </li>
              )}

              {/* Updated Info */}
              {updatedByName && (
                <li className="dropdown-item">
                  <FontAwesomeIcon icon={faPencil} /> {updatedByName}
                  {updatedAt &&
                    ` (${moment(updatedAt).format("DD-MMM-YY hh:mm A")})`}
                </li>
              )}
            </ul>
          </div>
        )
      }
    }



  ], [permissions]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    setPageSize,
    pageOptions,
    pageCount,
    gotoPage,
    state: { pageIndex, pageSize, globalFilter },
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 10 }
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  return (
    <div className="formcontainer mt-5">
      <div className='d-flex justify-content-between'>

        <h4>Area Project List</h4>
        {permissions?.read &&
          <h6>Total Count : {data.length}</h6>}
      </div>
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
        {/* Add Project Button */}
        {permissions?.write && (
          <button
            className="btn btn-outline-success mb-2"
            onClick={() => navigate('../addAreaProjects')}
          >
            Add Project
          </button>
        )}

        {/* Search + Page Size */}
        {permissions?.read && (
          <div className="d-flex align-items-center gap-3 flex-grow-1">
            {/* Global Filter */}
            <GlobalFilter
              globalFilter={globalFilter}
              setGlobalFilter={setGlobalFilter}
            />

            {/* Page Size Dropdown */}
            <label className="d-inline-flex align-items-center ">
            <select
              className="form-select w-auto"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {[10, 20, 50].map((size) => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </select>
            </label>
          </div>
        )}
      </div>

      {permissions?.read && (<>
        <div className=''>
          <table className="table  table-striped table-bordered table-hover " {...getTableProps()} style={{ fontSize: '13px', verticalAlign: 'baseline' }}>
            <thead className="table-light">
              {headerGroups.map(group => (
                <tr {...group.getHeaderGroupProps()}>
                  {group.headers.map(column => (
                    <th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      className="text-nowrap"
                    >
                      {column.render('Header')}
                      <span>
                        {column.isSorted
                          ? column.isSortedDesc
                            ? ' 🔽'
                            : ' 🔼'
                          : ''}
                      </span>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody {...getTableBodyProps()}>
              {page.map(row => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map(cell => (
                      <td className="text-nowrap" {...cell.getCellProps()}>
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
          <div className="d-flex align-items-center mb-3">
          {/* Previous – left corner */}
          <button
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
            className="btn btn-secondary me-auto"
            style={{ minWidth: 110 }}   // keeps width consistent
          >
            Previous
          </button>

          {/* Center block for page index & size selector */}
          <div className="text-center flex-grow-1 d-flex flex-column align-items-center">
            {/* Row 1: Page index and size selector in one line */}
            <div className="d-flex align-items-center gap-3">
              <div className="fw-semibold">
                Page {pageIndex + 1} of {pageOptions.length}
              </div>

            </div>
          </div>


          {/* Next – right corner */}
          <button
            onClick={() => nextPage()}
            disabled={!canNextPage}
            className="btn btn-secondary ms-auto"
            style={{ minWidth: 110 }}
          >
            Next
          </button>
        </div>
      </>)}
    </div>
  );
};

export default AreaProjectList;
